import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Divider, Form, Input, message, Modal, Select, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";
import { useProductRejects } from "../../../../../data/hooks/firestore/useProductRejects";
import QuantityControl from "components/shared-components/QuantityControl";
import useIsMobile from "../../../../../hooks/useIsMobile";
import ManageRejectReasonsModal from "./ManageRejectReasonsModal";
import { MobileModalFooter, MobileModalTitle, MobileSectionLabel } from "./mobileModalStyles";
import roles from "../../../../../roles";

const MIN_PRODUCT_BARCODE_LENGTH = 8;
const MAX_PRODUCT_BARCODE_LENGTH = 13;

const { Text } = Typography;

const selectStyles = `
  @media (max-width: 768px) {
    .select-16px .ant-select-selector input {
      font-size: 16px !important;
    }
    .select-16px .ant-select-selection-search-input {
      font-size: 16px !important;
    }
    .select-16px .ant-select-selection-item {
      font-size: 16px !important;
    }
    .select-16px .ant-select-selection-placeholder {
      font-size: 16px !important;
    }
    .ant-select-item.ant-select-item-option {
      font-size: 16px;
      padding-block: 8px;
    }
  }
`;

const RejectProductModal = ({ open, onCancel }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { uid, email, profile, role } = useAuth();
  const { products, getProductByBarcode } = useProducts();
  const { reasons, reasonsLoading, addRejectEvent } = useProductRejects();
  const isMobile = useIsMobile();
  const isAdmin = role === roles.admin || role === roles.superAdmin;

  const [submitting, setSubmitting] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [manageReasonsModalOpen, setManageReasonsModalOpen] = useState(false);

  const scanBufferRef = useRef("");
  const scanTimerRef = useRef(null);
  const barcodeInputRef = useRef(null);

  const selectedProductId = Form.useWatch("productId", form);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setQuantity(1);
      setBarcodeInput("");
      setTimeout(() => barcodeInputRef.current?.focus?.(), 50);
    } else {
      if (scanTimerRef.current) {
        clearTimeout(scanTimerRef.current);
        scanTimerRef.current = null;
      }
      scanBufferRef.current = "";
    }
  }, [open, form]);

  const handleProductBarcodeScanned = useCallback(
    (barcode) => {
      const trimmed = String(barcode || "").trim();
      if (!trimmed) return;

      if (trimmed.length < MIN_PRODUCT_BARCODE_LENGTH || trimmed.length > MAX_PRODUCT_BARCODE_LENGTH) {
        message.error(t("crate_stock.invalid_product_barcode"));
        return;
      }

      const product = getProductByBarcode?.(trimmed);
      if (!product) {
        message.error(t("crate_stock.unknown_product_barcode"));
        return;
      }

      form.setFieldsValue({ productId: product.id });
      setBarcodeInput(trimmed);
      message.success(`${product.productName || product.name?.en || product.id}`);
    },
    [form, getProductByBarcode, t],
  );

  useEffect(() => {
    if (!open) return;

    const clearScanBuffer = () => {
      scanBufferRef.current = "";
      if (scanTimerRef.current) {
        clearTimeout(scanTimerRef.current);
        scanTimerRef.current = null;
      }
    };

    const handleKeyDown = (e) => {
      if (!open) return;

      const target = e.target;
      const tag = (target?.tagName || "").toLowerCase();
      const isInSelect = target?.closest?.(".ant-select-dropdown");
      if (tag === "textarea" || isInSelect) return;

      if (tag === "input" && target !== barcodeInputRef.current) return;

      if (/^[0-9]$/.test(e.key)) {
        scanBufferRef.current = `${scanBufferRef.current}${e.key}`.slice(-MAX_PRODUCT_BARCODE_LENGTH);

        if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
        scanTimerRef.current = setTimeout(clearScanBuffer, 120);
        return;
      }

      if (e.key === "Enter") {
        const code = scanBufferRef.current;
        clearScanBuffer();
        if (code) handleProductBarcodeScanned(code);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearScanBuffer();
    };
  }, [open, handleProductBarcodeScanned]);

  const handleBarcodeInputChange = (e) => {
    setBarcodeInput(e.target.value);
  };

  const handleBarcodeInputPressEnter = () => {
    const trimmed = String(barcodeInput || "").trim();
    if (!trimmed) return;
    handleProductBarcodeScanned(trimmed);
  };

  const handleProductSelectChange = (productId) => {
    form.setFieldsValue({ productId });
    setBarcodeInput("");
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (quantity < 1) {
        message.error(t("crate_stock.quantity_min_one"));
        return;
      }

      setSubmitting(true);

      const product = products.find((p) => p.id === values.productId);
      const selectedReason = (reasons || []).find((r) => r.value === values.reason);
      const userName =
        profile?.displayName || profile?.name || profile?.fullName || profile?.email || email || uid || null;

      await addRejectEvent({
        productId: values.productId,
        productName: product?.productName || product?.name?.en || values.productId,
        quantity,
        reason: values.reason,
        reasonLabel: selectedReason?.label || values.reason,
        uid,
        email,
        userName,
      });

      message.success(t("crate_stock.reject_product_success"));
      form.resetFields();
      setQuantity(1);
      setBarcodeInput("");
      onCancel?.();
    } catch (e) {
      if (e?.errorFields) return;
      console.error("Error registering reject:", e);
      message.error(t("crate_stock.reject_product_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const productOptions = (products || []).map((p) => ({
    value: p.id,
    label: p.productName || p.name?.en || p.id,
  }));

  return (
    <>
      <style>{selectStyles}</style>
      <Modal
        title={
          isMobile ? (
            <MobileModalTitle>{t("crate_stock.reject_product_title")}</MobileModalTitle>
          ) : (
            t("crate_stock.reject_product_title")
          )
        }
        open={open}
        onCancel={onCancel}
        maskClosable={false}
        footer={
          isMobile ? (
            <MobileModalFooter
              secondary={{ label: t("cancel"), props: { onClick: onCancel, disabled: submitting } }}
              primary={{ label: t("crate_stock.submit"), props: { onClick: handleSubmit, loading: submitting } }}
            />
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <Space>
                <Button onClick={onCancel} disabled={submitting}>
                  {t("cancel")}
                </Button>
                <Button type="primary" onClick={handleSubmit} loading={submitting}>
                  {t("crate_stock.submit")}
                </Button>
              </Space>
            </div>
          )
        }
      >
        <Form layout="vertical" form={form}>
          <Form.Item label={t("crate_stock.scan_product_barcode_placeholder")}>
            <Input
              ref={barcodeInputRef}
              value={barcodeInput}
              onChange={handleBarcodeInputChange}
              onPressEnter={handleBarcodeInputPressEnter}
              placeholder={t("crate_stock.scan_product_barcode_placeholder")}
              inputMode="numeric"
              autoComplete="off"
              allowClear
              size={isMobile ? "large" : undefined}
              style={{ fontSize: 16 }}
            />
          </Form.Item>

          <Divider style={{ margin: "8px 0 16px" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("or")}
            </Text>
          </Divider>

          <Form.Item
            name="productId"
            label={t("crate_stock.product_type_label")}
            rules={[{ required: true, message: t("crate_stock.select_product") }]}
          >
            <Select
              showSearch
              placeholder={t("crate_stock.select_product_placeholder")}
              options={productOptions}
              onChange={handleProductSelectChange}
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              value={selectedProductId || undefined}
              size={isMobile ? "large" : undefined}
              className="select-16px"
              styles={{ popup: { root: { fontSize: 16 } } }}
            />
          </Form.Item>

          <div style={{ marginBottom: 20 }}>
            {isMobile ? (
              <MobileSectionLabel>{t("crate_stock.quantity_label")}</MobileSectionLabel>
            ) : (
              <div style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>{t("crate_stock.quantity_label")}</div>
            )}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
              <QuantityControl
                productId="reject-qty"
                currentQuantity={quantity}
                onIncrease={() => setQuantity((prev) => prev + 1)}
                onDecrease={() => setQuantity((prev) => Math.max(1, prev - 1))}
                onManualInput={(_, value) => setQuantity(Math.max(1, Number(value) || 1))}
                isMobile={isMobile}
                allowNegative={false}
              />
            </div>
          </div>

          <Form.Item
            name="reason"
            label={t("crate_stock.reject_reason_label")}
            rules={[{ required: true, message: t("crate_stock.select_reject_reason") }]}
          >
            <Select
              placeholder={t("crate_stock.select_reject_reason")}
              loading={reasonsLoading}
              options={reasons || []}
              size={isMobile ? "large" : undefined}
              className="select-16px"
              styles={{ popup: { root: { fontSize: "26px !important" } } }}
            />
          </Form.Item>

          {isAdmin && (
            <div style={{ marginTop: -8, marginBottom: 16 }}>
              <Button
                size={"small"}
                type={isMobile ? "link" : "default"}
                style={isMobile ? { padding: 0 } : undefined}
                onClick={() => setManageReasonsModalOpen(true)}
              >
                {t("crate_stock.manage_reject_reasons")}
              </Button>
            </div>
          )}
        </Form>

        <ManageRejectReasonsModal open={manageReasonsModalOpen} onCancel={() => setManageReasonsModalOpen(false)} />
      </Modal>
    </>
  );
};

export default RejectProductModal;
